import {
    FunctionToolArrayItemType,
    FunctionToolDefinition,
    FunctionToolParameter,
    FunctionToolParameterType,
} from '../../../../domain/agents/models/FunctionTool';

export interface FunctionToolFormParameter {
    name: string;
    type: FunctionToolParameterType | '';
    description: string;
    required: boolean;
    enumValues: string;
    properties: FunctionToolFormParameter[];
    itemsType: FunctionToolArrayItemType | '';
    items: FunctionToolFormParameter[];
}

export interface FunctionToolFormDefinition {
    name: string;
    description: string;
    strictMode: boolean;
    parameters: FunctionToolFormParameter[];
}

export const createDefaultFunctionToolFormParameter = (): FunctionToolFormParameter => ({
    name: '',
    type: '',
    description: '',
    required: true,
    enumValues: '',
    properties: [],
    itemsType: '',
    items: [],
});

export const createDefaultFunctionToolFormDefinition = (): FunctionToolFormDefinition => ({
    name: '',
    description: '',
    strictMode: false,
    parameters: [createDefaultFunctionToolFormParameter()],
});

const splitEnumValues = (value: string): string[] | undefined => {
    const parsed = value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

    return parsed.length > 0 ? parsed : undefined;
};

const mapFormParameterToRequest = (parameter: FunctionToolFormParameter): FunctionToolParameter | null => {
    const name = parameter.name.trim();
    const type = parameter.type;

    if (!name || !type) {
        return null;
    }

    const result: FunctionToolParameter = {
        type,
        description: parameter.description.trim() || undefined,
        required: parameter.required,
    };

    const enumValues = splitEnumValues(parameter.enumValues);
    if (type === 'string' && enumValues) {
        result.enum = enumValues;
    }

    if (type === 'object') {
        const properties = mapFormParametersToRequest(parameter.properties);
        if (Object.keys(properties).length > 0) {
            result.properties = properties;
        }
    }

    if (type === 'array' && parameter.itemsType) {
        const items: FunctionToolParameter = {
            type: parameter.itemsType,
        };

        if (parameter.itemsType === 'object') {
            const properties = mapFormParametersToRequest(parameter.items);
            if (Object.keys(properties).length > 0) {
                items.properties = properties;
            }
        }

        result.items = items;
    }

    return result;
};

export const mapFormParametersToRequest = (
    parameters: FunctionToolFormParameter[],
): Record<string, FunctionToolParameter> => {
    return parameters.reduce<Record<string, FunctionToolParameter>>((accumulator, parameter) => {
        const mapped = mapFormParameterToRequest(parameter);
        const name = parameter.name.trim();

        if (!mapped || !name) {
            return accumulator;
        }

        accumulator[name] = mapped;
        return accumulator;
    }, {});
};

export const mapFunctionToolsFormToRequest = (
    functionTools: FunctionToolFormDefinition[],
): FunctionToolDefinition[] => {
    return functionTools.reduce<FunctionToolDefinition[]>((accumulator, functionTool) => {
        const name = functionTool.name.trim();
        if (!name) {
            return accumulator;
        }

        accumulator.push({
            name,
            description: functionTool.description.trim() || undefined,
            strictMode: functionTool.strictMode,
            parameters: mapFormParametersToRequest(functionTool.parameters),
        });
        return accumulator;
    }, []);
};

const mapRequestParameterToForm = (name: string, parameter: FunctionToolParameter): FunctionToolFormParameter => ({
    name,
    type: parameter.type,
    description: parameter.description ?? '',
    required: parameter.required ?? false,
    enumValues: parameter.enum?.join(', ') ?? '',
    properties: parameter.properties
        ? Object.entries(parameter.properties).map(([propertyName, property]) =>
              mapRequestParameterToForm(propertyName, property),
          )
        : [],
    itemsType:
        parameter.type === 'array' && parameter.items?.type && parameter.items.type !== 'array'
            ? parameter.items.type
            : '',
    items:
        parameter.type === 'array' && parameter.items?.type === 'object' && parameter.items.properties
            ? Object.entries(parameter.items.properties).map(([propertyName, property]) =>
                  mapRequestParameterToForm(propertyName, property),
              )
            : [],
});

export const mapFunctionToolsRequestToForm = (
    functionTools?: FunctionToolDefinition[],
): FunctionToolFormDefinition[] => {
    if (!functionTools || functionTools.length === 0) {
        return [];
    }

    return functionTools.map((functionTool) => ({
        name: functionTool.name,
        description: functionTool.description ?? '',
        strictMode: functionTool.strictMode ?? false,
        parameters: Object.entries(functionTool.parameters ?? {}).map(([name, parameter]) =>
            mapRequestParameterToForm(name, parameter),
        ),
    }));
};
